import type { JWTPayload } from 'jose';

const ACTIVE_CLIENT_COOKIE = 'ciam_active_client';
const COOKIE_PREFIX = 'ciam_';

export interface ActiveClient {
  clientId: string;
}

interface ActiveClientCookie {
  clients?: Array<{ id?: string; idp?: string }>;
}

// Reads the FusionAuth client from the `ciam_active_client` cookie, or null.
export function getActiveClient(): ActiveClient | null {
  const raw = getCookieByName(ACTIVE_CLIENT_COOKIE);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(raw)) as ActiveClientCookie;
    const active = parsed.clients?.find((c) => c.idp === 'fusionauth');
    return active?.id ? { clientId: active.id } : null;
  } catch {
    return null;
  }
}

export function getCookie(cookieName: string): string | null {
  return getCookieByName(`${COOKIE_PREFIX}${cookieName}`);
}

// Attributes (`Path=/; Secure; SameSite=Lax`) mirror the auth worker's `Set-Cookie` output so
// the browser matches the expiry write to the same cookie identity that was set server-side.
export function deleteCookie(cookieName: string): void {
  const attrs = 'Path=/; Max-Age=0; Secure; SameSite=Lax';
  document.cookie = `${COOKIE_PREFIX}${cookieName}=; ${attrs}`;
}

function getCookieByName(fullCookieName: string): string | null {
  const cookies = document.cookie.split(';');
  for (const c of cookies) {
    const [name, ...value] = c.split('=');
    if (name.trim() === fullCookieName) {
      return value.join('=');
    }
  }
  return null;
}

export async function extractToken<T extends JWTPayload>(cookieName: string): Promise<T | null> {
  const cookie = getCookie(cookieName);

  if (!cookie) {
    return null;
  }

  const { decodeJwt } = await import('../vendor/jose');

  return decodeJwt(cookie) as T;
}
